import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { deleteStory, getZonesAndRooms, saveStory } from "../../../../api/museum";
import Dropdown from "../../../../components/common/Dropdown";
import TitleSubtitle from "../../../../components/common/TitleSubtitle";
import { useTrackCreation } from "../../../../context/TrackCreationContext";
import { Zone, ClickableZone, SubPlan } from "../../../../types/types";
import Button from "../../../../components/ui/Button";
import { MuseumContentProps } from "../../../../types/screensTypes";
import StoryList from "../../../../components/lists/MuseumStoryList";
import shell from "../createShell.module.css";
import mapStyles from "../createMap.module.css";
import stepStyles from "../createStep.module.css";
import storiesStyles from "./Stories.module.css";

const MuseumContent: React.FC<MuseumContentProps> = ({ navigateToNext, navigateToContinue }) => {
    const { trackData } = useTrackCreation();
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const location = useLocation();

    const [rooms, setRooms] = useState<SubPlan[]>([]);
    const [zones, setZones] = useState<Record<string, Zone[]>>({});
    const [zonesDropDown, setZonesDropDown] = useState<{ label: string; value: string }[]>([]);
    const [roomsDropDown, setRoomsDropDown] = useState<{ label: string; value: string }[]>([]);
    const [selectedZone, setSelectedZone] = useState<string | null>(null);
    const [selectedRoom, setSelectedRoom] = useState<string | null>(null);
    const [selectedRoomData, setSelectedRoomData] = useState<SubPlan | null>(null);
    const [storiesForSubPlan, setStoriesForSubPlan] = useState<ClickableZone[]>([]);
    const [storiesFilteredForSubPlan, setStoriesFilteredForSubPlan] = useState<ClickableZone[]>([]);
    const [chosenImageWidth, setChosenImageWidth] = useState<number>(0);
    const [chosenImageHeight, setChosenImageHeight] = useState<number>(0);

    const getZonesAndRoomsByTrackId = async (trackId: string) => {
        const token = localStorage.getItem("token");
        if (!token) return;
        const response = await getZonesAndRooms({ token, trackId });

        if (response) {
            const roomsList: SubPlan[] = response.subPlans;

            setRooms(roomsList);

            const roomsForDropdown = roomsList
                .sort((a, b) => a.floorNumber - b.floorNumber)
                .map((room) => ({
                    label: room.internalName?.trim() || `${t("floorNumber")} ${room.floorNumber}`,
                    value: room.id,
                }));

            setRoomsDropDown(roomsForDropdown);

            const zonesByRoom = roomsList.reduce<Record<string, Zone[]>>((acc, room) => {
                acc[room.id] = response?.subPlansZones?.filter((zone: Zone) => zone.subPlansId === room.id) || [];
                return acc;
            }, {});

            setZones(zonesByRoom);
        }
    };

    useEffect(() => {
        if (location.state && location.state.action) {
            setSelectedRoom(location.state.action);
        }
    }, [location.state]);

    useEffect(() => {
        if (zones && selectedRoom) {
            const zonesForDropdown =
                zones[selectedRoom]?.map((zone) => ({
                    label: zone.internalName?.trim() || `Zone ${zone.position ?? ""}`.trim(),
                    value: zone.id,
                })) || [];
            setZonesDropDown(zonesForDropdown);
        } else {
            setZonesDropDown([]);
        }
    }, [zones, selectedRoom]);

    useEffect(() => {
        if (trackData && trackData.trackId) {
            getZonesAndRoomsByTrackId(trackData.trackId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trackData.trackId]);

    const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
        setChosenImageWidth(event.currentTarget.width);
        setChosenImageHeight(event.currentTarget.height);
    };

    useEffect(() => {
        if (selectedRoom) {
            const detailedRoom = rooms.find((room) => room.id === selectedRoom);
            setSelectedRoomData(detailedRoom || null);

            if (detailedRoom) {
                const scale = chosenImageWidth / detailedRoom.widthPx;
                const offsetX = chosenImageWidth / 2;
                const offsetY = chosenImageHeight / 2;
                const markerSize = 20;

                const storiesSubPlan = detailedRoom.clickableZones
                    .map((item) => ({
                        ...item,
                        displayCoordX: item.coordX * scale + offsetX - markerSize / 2,
                        displayCoordY: item.coordY * scale + offsetY - markerSize / 2,
                    }))
                    .sort((a, b) => a.position - b.position);

                setStoriesForSubPlan(storiesSubPlan);
            }
        }
    }, [selectedRoom, chosenImageWidth, chosenImageHeight, rooms]);

    // Filter stories based on selected zone
    useEffect(() => {
        if (selectedZone && storiesForSubPlan.length > 0) {
            const storiesFiltered = storiesForSubPlan.filter((item) => item.subPlansZonesId === selectedZone);
            setStoriesFilteredForSubPlan(storiesFiltered);
        } else {
            setStoriesFilteredForSubPlan(storiesForSubPlan);
        }
    }, [selectedZone, storiesForSubPlan]);

    // Handle clicking on the image
    const handleImageClick = async (event: React.MouseEvent<HTMLImageElement>) => {
        console.log("Image clicked:", event);
        const image = event.currentTarget;
        const imageRect = image.getBoundingClientRect();
        const detailedRoom = rooms.find((room) => room.id === selectedRoom);
        if (!detailedRoom) return;
        const offsetX = event.clientX - imageRect.left;
        const offsetY = event.clientY - imageRect.top;
        const offsetImageX = chosenImageWidth / 2;
        const offsetImageY = chosenImageHeight / 2;
        const scale = chosenImageWidth / (detailedRoom.widthPx ? detailedRoom.widthPx : 1);
        const halfMarkerSize = 10;
        const updatedX = (offsetX - offsetImageX + halfMarkerSize) / scale;
        const updatedY = (offsetY - offsetImageY + halfMarkerSize) / scale;

        // Check if click was on an existing marker
        const clickedZone = storiesFilteredForSubPlan.find((item) => {
            const markerX = item.coordX;
            const markerY = item.coordY;
            return Math.abs(updatedX - markerX) <= 10 && Math.abs(updatedY - markerY) <= 10;
        });

        console.log("Clicked Zone:", clickedZone);

        if (clickedZone) {
            navigateToNext(clickedZone.id, clickedZone.subPlansZonesId);
        } else {
            const maxPosition = storiesFilteredForSubPlan.length
                ? Math.max(...storiesFilteredForSubPlan.map((story) => story.position)) + 1
                : 1;
            console.log("Adding new story at position:", maxPosition);
            console.log("Updated Coordinates:", updatedX, updatedY);
            // If no existing marker was clicked, create a new story
            await saveStoryAPI(updatedX, updatedY, maxPosition);
        }
    };

    // API Calls
    const saveStoryAPI = async (coordinatesX: number, coordinatesY: number, position: number) => {
        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;
        const subplanId = selectedRoom;
        if (!token || !trackId || !subplanId) return;

        // Once any room exists in the venue, stories must be linked to a room
        const museumHasAnyRoom = Object.values(zones).some((floorZones) => floorZones.length > 0);
        if (museumHasAnyRoom && !selectedZone) {
            return;
        }

        await saveStory({
            token,
            trackId: trackId,
            subplanId: subplanId,
            zoneId: selectedZone || undefined,
            coordX: coordinatesX,
            coordY: coordinatesY,
            position,
        });
        await getZonesAndRoomsByTrackId(trackId);
    };

    const saveStoryPositionAPI = async (planId: string, selectedPosition: number) => {
        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;
        const subplanId = selectedRoom;
        if (!token || !trackId || !subplanId) return;

        await saveStory({
            token,
            trackId: trackId,
            clickableZoneId: planId,
            subplanId: subplanId,
            position: selectedPosition,
        });
        await getZonesAndRoomsByTrackId(trackId);
    };

    const saveStoryCoordinatesAPI = async (storyId: string, coordinatesX: number, coordinatesY: number) => {
        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;
        const subplanId = selectedRoom;

        console.log("Saving story coordinates:", storyId, coordinatesX, coordinatesY, trackId, subplanId);
        if (!token || !trackId || !subplanId) return;

        await saveStory({
            token,
            trackId: trackId,
            clickableZoneId: storyId,
            subplanId: subplanId,
            coordX: coordinatesX,
            coordY: coordinatesY,
        });
        await getZonesAndRoomsByTrackId(trackId);
    };

    const deleteStoryAPI = async (storyId: string) => {
        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;
        if (!token || !trackId) return;

        const response = await deleteStory({
            token,
            trackId,
            clickableZoneId: storyId,
        });
        if (response) {
            await getZonesAndRoomsByTrackId(trackId);
        }
    };

    // Drag & Drop Handlers
    const handleDragOver = (event: React.DragEvent<HTMLImageElement>) => {
        event.preventDefault();
    };

    const handleDrag = (event: React.DragEvent<HTMLDivElement>) => {
        // Prevent default behavior to enable dropping
        event.preventDefault();
    };

    const handleDrop = async (event: React.DragEvent<HTMLImageElement>) => {
        event.preventDefault();

        console.log("Drop event:", event);
        const trackId = trackData.trackId;
        if (!trackId || !selectedRoom) return;

        const detailedRoom = rooms.find((room) => room.id === selectedRoom);
        const itemId = event.dataTransfer.getData("itemId");
        const image = event.currentTarget;
        const imageRect = image.getBoundingClientRect();

        const offsetX = event.clientX - imageRect.left;
        const offsetY = event.clientY - imageRect.top;
        const offsetImageX = chosenImageWidth / 2;
        const offsetImageY = chosenImageHeight / 2;
        const scale = chosenImageWidth / (detailedRoom?.widthPx ? detailedRoom.widthPx : 1);
        const halfMarkerSize = 10;
        const updatedX = (offsetX - offsetImageX + halfMarkerSize) / scale;
        const updatedY = (offsetY - offsetImageY + halfMarkerSize) / scale;

        console.log("Dropped item ID:", itemId);
        console.log("Updated coordinates:", updatedX, updatedY);
        console.log(offsetX, offsetY, offsetImageX, offsetImageY, scale);
        console.log(trackData?.widthPx);
        if (!itemId) return;

        await saveStoryCoordinatesAPI(itemId, updatedX, updatedY);
        await getZonesAndRoomsByTrackId(trackId);
    };

    const handleDragStart = (event: React.DragEvent<HTMLDivElement>, itemId: string) => {
        event.dataTransfer.setData("itemId", itemId);
    };

    const zonesForSelectedFloor = selectedRoom ? zones[selectedRoom] || [] : [];
    const hasZonesOnFloor = zonesForSelectedFloor.length > 0;
    const museumHasAnyRoom = Object.values(zones).some((floorZones) => floorZones.length > 0);
    // Once any room exists in the venue, every story must be linked to a room
    const roomsRequiredGlobally = museumHasAnyRoom;
    const needsRoomSelection = Boolean(selectedRoom && roomsRequiredGlobally && hasZonesOnFloor && !selectedZone);
    const needsRoomOnFloorFirst = Boolean(selectedRoom && roomsRequiredGlobally && !hasZonesOnFloor);

    // Add a new story without a plan (list / no visual representation mode)
    const addStoryNoPlan = async () => {
        if (roomsRequiredGlobally && !selectedZone) return;

        const maxPosition = storiesFilteredForSubPlan.length
            ? Math.max(...storiesFilteredForSubPlan.map((story) => story.position)) + 1
            : 1;

        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;
        if (!token || !trackId || !selectedRoom) return;

        await saveStory({
            token,
            trackId,
            subplanId: selectedRoom,
            zoneId: selectedZone || undefined,
            coordX: 0.0,
            coordY: 0.0,
            position: maxPosition,
        });
        await getZonesAndRoomsByTrackId(trackId);
    };

    return (
        <div className={shell.mapShell}>
            <div className={mapStyles.leftColumn}>
                <div className={mapStyles.titleBlock}>
                    <TitleSubtitle
                        title={t("museumStories")}
                        subtitle={t("addStoriesByClickingOnThePlan")}
                        layout="stories"
                    />
                </div>

                <div className={storiesStyles.fields}>
                    <Dropdown
                        label=""
                        value={selectedRoom}
                        options={roomsDropDown}
                        onChange={(value) => {
                            setSelectedZone(null);
                            setStoriesFilteredForSubPlan([]);
                            setSelectedRoom(value);
                        }}
                        placeholder={t("pleaseSelectAPlan")}
                        loading={false}
                        error={false}
                        isWithoutMargin
                    />

                    {zonesDropDown && zonesDropDown.length > 0 && (
                        <Dropdown
                            label=""
                            value={selectedZone}
                            options={zonesDropDown}
                            onChange={(value) => setSelectedZone(value)}
                            placeholder={t("pleaseSelectARoom")}
                            loading={false}
                            error={false}
                            isWithoutMargin
                        />
                    )}

                    {needsRoomSelection && (
                        <p className={storiesStyles.hintWarn} role="status">
                            {t("pleaseSelectARoomBeforeAddingStory")}
                        </p>
                    )}

                    {needsRoomOnFloorFirst && (
                        <p className={storiesStyles.hintWarn} role="status">
                            {t("createRoomOnFloorBeforeAddingStory")}
                        </p>
                    )}

                    {selectedRoomData &&
                        trackData.isMuseumList === true &&
                        !needsRoomSelection &&
                        !needsRoomOnFloorFirst && (
                            <Button
                                className={storiesStyles.addBtn}
                                label={t("addANewStory")}
                                onClick={async () => await addStoryNoPlan()}
                            />
                        )}
                </div>

                {selectedRoomData && trackData.isMuseumList === false && !needsRoomOnFloorFirst && (
                    <div className={storiesStyles.plan}>
                        <img
                            src={selectedRoomData.planUrl}
                            alt={selectedRoomData.internalName}
                            className={storiesStyles.planImg}
                            onLoad={handleImageLoad}
                            onClick={handleImageClick}
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                        />
                        <div className={storiesStyles.planOverlay}>
                            {storiesFilteredForSubPlan &&
                                storiesFilteredForSubPlan.length > 0 &&
                                storiesFilteredForSubPlan.map((item) => (
                                    <div
                                        key={item.id}
                                        className={storiesStyles.pin}
                                        style={{
                                            left: `${item.displayCoordX}px`,
                                            top: `${item.displayCoordY}px`,
                                        }}
                                        draggable="true"
                                        onDragStart={(event) => handleDragStart(event, item.id)}
                                        onDrag={(event) => handleDrag(event)}
                                    >
                                        <span>{item.position}</span>
                                    </div>
                                ))}
                        </div>
                    </div>
                )}
            </div>

            {selectedRoomData && (
                <div className={`${mapStyles.poiColumn} ${storiesStyles.poiSidebar}`}>
                    <div className={storiesStyles.poiListWrap}>
                        <div className={mapStyles.waypointsHeader}>
                            <h3>{t("stories")}</h3>
                        </div>
                        {storiesFilteredForSubPlan && (
                            <StoryList
                                clickableZones={storiesFilteredForSubPlan}
                                setClickableZones={setStoriesForSubPlan}
                                navigateToNext={navigateToNext}
                                deleteClickableZone={(storyId) => {
                                    deleteStoryAPI(storyId);
                                }}
                                saveClickableZone={(planId, selectedPosition) => {
                                    saveStoryPositionAPI(planId, selectedPosition);
                                }}
                            />
                        )}
                    </div>
                </div>
            )}

            {navigateToContinue && (
                <div className={`${stepStyles.stepButton} ${stepStyles.footer} ${stepStyles.footerMap}`}>
                    <Button
                        label={t("continue")}
                        onClick={navigateToContinue}
                        variant="primary"
                        shape="pill"
                        className={`${stepStyles.continueBtn} ${mapStyles.mapBtn}`}
                    />
                </div>
            )}
        </div>
    );
};

export default MuseumContent;
